import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import GradingIcon from '@mui/icons-material/Grading'
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors'

import { Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { formatDate } from 'date-fns'
import { formatCPFOrCNPJ } from 'src/utils/formatter-utils'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import IntegracaoMonitoramentoScope from '../integracao-monitoramento.scopes'
import { MonitoramentoRow } from '../integracao-monitoramento.types'

const LOG = Logger.get('GRUPO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type IntegracaoMonitoramentoViewProps = {
    className?: string
    scope: IntegracaoMonitoramentoScope
}
class IntegracaoMonitoramentoViewClass implements FCClassContext<IntegracaoMonitoramentoViewProps> {
    // :: emissors

    readonly emitOnFind = () => {
        this.scope.docFilterInputRef = this.docFilterInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    readonly emitOnNew = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly handleEditClick = (id: GridRowId) => () => {
        this.scope.onEdit(id as number).catch(LOG.caught)
    }

    readonly handleDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(id as number).catch(LOG.caught)
    }

    readonly emitReprocessarClick = (id: GridRowId) => () => {
        this.scope.onReprocess(id as number).catch(LOG.caught)
    }

    readonly emitShowResultsClick = (id: GridRowId) => () => {
        this.scope.onShowResults(id as number).catch(LOG.caught)
    }

    // :: Fields
    scope!: IntegracaoMonitoramentoScope
    sytles!: ReturnType<typeof useStyles>
    docFilterInputRef!: React.RefObject<HTMLInputElement | null>
    monitoramentosColumns = static_build_monitoramentos_columns(
        this.handleEditClick,
        this.handleDeleteClick,
        this.emitReprocessarClick,
        this.emitShowResultsClick
    )

    // :: Methods

    onSyncState({ scope }: IntegracaoMonitoramentoViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: IntegracaoMonitoramentoViewProps) {
        this.scope = bindUpdate(React, scope)
        this.docFilterInputRef = React.useRef<HTMLInputElement>(null)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '10px'
                    }}
                >
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.docFilterInputRef}
                        label="Filtre pelo nome do grupo de análise"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                    <Button variant="outlined" onClick={this.emitOnNew}>
                        Novo
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <DataGrid
                        rows={this.scope.monitorados}
                        columns={this.monitoramentosColumns}
                        getRowId={static_getMonitoradosRowId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />
                </Paper>
            </div>
        )
    }
}

export const IntegracaoMonitoramentoView = classToFComponent(IntegracaoMonitoramentoViewClass, React)
export default IntegracaoMonitoramentoView

function static_build_monitoramentos_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void,
    emitReprocessarClick: (id: GridRowId) => () => void,
    emitShowResultsClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'doc',
            headerName: 'Documento',
            width: 150,
            valueFormatter: (value: string) => {
                return formatCPFOrCNPJ(value)
            }
        },
        {
            field: 'cedente_nome',
            headerName: 'Nome',
            width: 250
        },
        {
            field: 'grupo_nome',
            headerName: 'Grupo Proc.',
            width: 120
        },
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 200,
            valueFormatter: (value: string) => {
                return value === 'BLOQUEADO' ? 'Bloqueado' : 'Monitorado Visão Cedente'
            }
        },
        {
            field: 'data_processamento',
            headerName: 'Inicio Proc.',
            width: 100,
            valueFormatter: (value: string) => {
                return !value ? '' : formatDate(value, 'dd/MM/yyyy HH:mm')
            }
        },
        {
            field: 'data_termino_processamento',
            headerName: 'Fim Proc.',
            width: 100,
            valueFormatter: (value: string) => {
                return !value ? '' : formatDate(value, 'dd/MM/yyyy HH:mm')
            }
        },
        {
            field: 'status_processamento',
            headerName: 'Status',
            width: 100,
            valueFormatter: (value: string) => {
                return value === 'PENDENTE' ? 'Pendente' : value === 'PROCESSED' ? 'Processado' : 'Processando'
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                const arrActions = [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]

                if (row.tipo === 'MONITORADO_VISAO_CEDENTE') {
                    arrActions.push(
                        <GridActionsCellItem
                            key={'reproc' + id}
                            icon={<RunningWithErrorsIcon />}
                            label="Reprocessar"
                            className="textPrimary"
                            onClick={emitReprocessarClick(id)}
                            color="inherit"
                        />,
                        <GridActionsCellItem
                            key={'result' + id}
                            icon={<GradingIcon />}
                            label="Exibir Resultado"
                            className={'displaynone'}
                            disabled={row.status_processamento !== 'PROCESSED'}
                            onClick={emitShowResultsClick(id)}
                            color="inherit"
                        />
                    )
                }
                return arrActions
            }
        }
    ]
}

function static_getMonitoradosRowId(row: MonitoramentoRow) {
    return row.id + ''
}
